import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Star, Check } from 'lucide-react';
import {
  sendFeedbackEmail,
  getUserByCogId 
} from "api/user";

const FeedbackForm = ({ variant = "default", label = "Feedback" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const user = await getUserByCogId();
        
        // Add null/undefined checks
        if (user && user.email) {
          setUserEmail(user.email);
        } else {
          console.warn('User data or email not found:', user);
          // Optionally set a fallback or handle the case where email is not available
          setUserEmail(null);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        // Set userEmail to null on error to prevent form submission
        setUserEmail(null);
      }
    };
    fetchUserEmail();
  }, []);

  const handleSubmit = async () => {
    // Add check for userEmail availability
    if (!userEmail) {
      alert('User email not available. Please try refreshing the page.');
      return;
    }

    if (rating === 0 || feedbackType === '' || description.trim() === '') {
      alert('Please fill in all fields');
      return;
    }

    if (file && file.size > 5 * 1024 * 1024) {
      alert("File size exceeds 5MB limit");
      return;
    }

    const formData = new FormData();
    formData.append('rating', rating);
    formData.append('feedbackType', feedbackType);
    formData.append('description', description);
    formData.append('userEmail', userEmail);
    if (file) {
      formData.append('file', file);
    }

    try {
      const response = await sendFeedbackEmail(formData);

      if (response.message && response.message.includes('successfully')) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsOpen(false);
          setIsSubmitted(false);
          setRating(0);
          setFeedbackType('');
          setDescription('');
          setFile(null);
        }, 3000);
      } else {
        alert("Something went wrong while submitting feedback.");
      }
    } catch (err) {
      console.error("Error submitting feedback:", err);
      alert("Error submitting feedback.");
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSubmitted(false);
    setRating(0);
    setFeedbackType('');
    setDescription('');
    setFile(null);
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starNumber = index + 1;
      return (
        <Star
          key={starNumber}
          className={`w-8 h-8 cursor-pointer transition-colors duration-200 ${
            starNumber <= (hoveredRating || rating)
              ? 'fill-yellow-400 text-yellow-400'
              : 'text-gray-300 hover:text-yellow-400'
          }`}
          onClick={() => setRating(starNumber)}
          onMouseEnter={() => setHoveredRating(starNumber)}
          onMouseLeave={() => setHoveredRating(0)}
        />
      );
    });
  };

  return (
    <>
      {variant === "floating" ? (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-full shadow-lg border border-gray-200 hover:shadow-xl hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400"
          aria-label="Open feedback form"
        >
          <MessageCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
          <span>{label}</span>
        </button>
      ) : (
        <div className="w-full">
          <div className="flex items-center justify-center">
            <div className="relative inline-flex group w-full">
              <div className="absolute transition-all duration-1000 opacity-70 -inset-px bg-gradient-to-r from-[#44BCFF] via-[#FF44EC] to-[#FF675E] rounded-lg blur-sm group-hover:opacity-100 group-hover:-inset-1 group-hover:duration-200" />
              <button
                onClick={() => setIsOpen(true)}
                className="relative inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 shadow-sm hover:shadow-md"
              >
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                <span>{label}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all duration-300 scale-100 opacity-100">
            <div className="relative p-6 pb-4">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
              
              <h2 className="text-2xl text-center font-bold text-gray-800 mb-4">
                Feedback Form
              </h2>
              
              {/* Conditional text based on submission status */}
              {!isSubmitted ? (
                <>
                  <p className="text-gray-600 text-md font-medium leading-relaxed">
                    Your feedback is valuable in providing you our services, thanks for filling out the feedback form below.
                  </p>
                  {userEmail ? (
                    <p className="text-sm text-gray-500 mt-2">
                      Submitting as: {userEmail}
                    </p>
                  ) : (
                    <p className="text-sm text-red-500 mt-2">
                      Loading user information...
                    </p>
                  )}
                </>
              ) : (
                <p className="text-gray-600 text-md font-medium leading-relaxed text-center">
                  Thank you for taking the time to provide your valuable feedback!
                </p>
              )}
            </div>

            <div className="px-6 pb-6">
              {!isSubmitted ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-md font-semibold text-gray-900 mb-3">
                      How is your experience on this platform?
                    </label>
                    <div className="flex gap-1 mb-2">
                      {renderStars()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-md font-semibold text-gray-900 mb-2">
                      Feedback Type
                    </label>
                    <select
                      value={feedbackType}
                      onChange={(e) => setFeedbackType(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select feedback type...</option>
                      <option value="review existing template">Review existing template</option>
                      <option value="request for additional template">Request for additional template</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-md font-semibold text-gray-900 mb-2">
                      Feedback Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="Please provide your detailed feedback..."
                    />
                  </div>

                  {/* File upload */}
                  <div>
                    <label className="block text-md font-semibold text-gray-900 mb-2">
                      Attach a file (optional, max 5MB)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const selectedFile = e.target.files[0];
                        if (selectedFile && selectedFile.size > 5 * 1024 * 1024) {
                          alert("File size exceeds 5MB limit");
                        } else {
                          setFile(selectedFile);
                        }
                      }}
                      className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                    />
                    {file && (
                      <p className="text-sm text-gray-500 mt-1">Selected: {file.name}</p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleClose}
                      className="flex-1 px-6 py-3 border bg-rose-200 border-gray-200 text-gray-900 rounded-lg hover:bg-rose-400 transition-colors duration-200 font-medium"
                    >
                      Close
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!userEmail}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-primary to-purple-300 text-black rounded-lg hover:from-blue-500 hover:to-purple-600 transition-all duration-200 font-bold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {userEmail ? 'Submit' : 'Loading...'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Thank you!
                  </h3>
                  <p className="text-gray-600">
                    Your form has been submitted successfully.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackForm;